import React from "react"
import { Divider, Flex, Input, Radio, Spin, Tooltip } from "antd"
import { InfoCircleOutlined, LoadingOutlined } from "@ant-design/icons"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { Text, Title } from "ui/Typography"
import { numberToCurrency } from "helpers/string"
import styles from "./index.module.css"

const PaymentBreakdown = ({ breakdownData, breakdownLoading, refundType, partialAmount, partialAmountError, isRefundDisabled, handleRadioChange, handlePartialAmountChange }) => {
  const { t } = useTranslation()

  if (breakdownLoading) {
    return (
      <Flex justify="center" align="center" className={styles.breakdownLoader}>
        <Spin indicator={<LoadingOutlined spin />} />
      </Flex>
    )
  }
  if (!breakdownData) {
    return <Text className={styles.errorText}>{t("bookings.cancel_modal.breakdown_load_error")}</Text>
  }

  const totalTaxes =
    (breakdownData.tax_and_fee || 0) +
    (breakdownData.custom_fees || []).reduce((sum, fee) => sum + (fee.price || 0), 0)

  const taxesTooltipContent = (
    <Flex vertical gap={4}>
      {breakdownData.tax_and_fee > 0 && (
        <Flex justify="space-between" gap={24}>
          <span>{t("bookings.cancel_modal.taxes_and_fees")}</span>
          <span>{numberToCurrency(breakdownData.tax_and_fee)}</span>
        </Flex>
      )}
      {(breakdownData.custom_fees || []).map((fee) => (
        <Flex key={fee.id} justify="space-between" gap={24}>
          <span>{fee.title}</span>
          <span>{numberToCurrency(fee.price)}</span>
        </Flex>
      ))}
    </Flex>
  )

  return (
    <>
      <Title level={5} className={styles.sectionTitle}>{t("bookings.cancel_modal.breakdown_title")}</Title>
      <Flex vertical gap={12} className={styles.breakdownSection}>
        <Flex justify="space-between" align="center" className={styles.breakdownRow}>
          <Text>{t("bookings.cancel_modal.total_booking_amount")}</Text>
          <Text strong>{numberToCurrency(breakdownData.total_price || 0)}</Text>
        </Flex>
        <Flex justify="space-between" align="center" className={styles.breakdownRow}>
          <Flex align="center" gap={6}>
            <Text>{t("bookings.cancel_modal.taxes_and_fees")}</Text>
            {breakdownData.tax_and_fee > 0 && (
              <Tooltip title={taxesTooltipContent} placement="right">
                <InfoCircleOutlined className={styles.infoIcon} />
              </Tooltip>
            )}
          </Flex>
          <Text strong>{numberToCurrency(totalTaxes)}</Text>
        </Flex>
        <Flex justify="space-between" align="center" className={styles.breakdownRow}>
          <Text>{t("bookings.cancel_modal.amount_paid")}</Text>
          <Text strong>{numberToCurrency(breakdownData.amount_paid_by_guest || 0)}</Text>
        </Flex>
        {breakdownData.amount_refunded > 0 && (
          <Flex justify="space-between" align="center" className={styles.breakdownRow}>
            <Text>{t("bookings.cancel_modal.amount_refunded")}</Text>
            <Text strong>{numberToCurrency(breakdownData.amount_refunded)}</Text>
          </Flex>
        )}
        <Divider className={styles.breakdownDivider} />
        <Flex justify="space-between" align="center" className={`${styles.breakdownRow} ${styles.refundableRow}`}>
          <Text strong>{t("bookings.cancel_modal.refundable_amount")}</Text>
          <Text strong className={styles.refundableAmount}>{numberToCurrency(breakdownData.refundable_amount || 0)}</Text>
        </Flex>
      </Flex>
      <Flex vertical gap={8} className={styles.refundOptions}>
        <Radio.Group value={refundType} onChange={handleRadioChange}>
          <Flex gap={16}>
            <Radio value="full" disabled={isRefundDisabled}><Text strong>{t("bookings.cancel_modal.full_refund_label")}</Text></Radio>
            <Radio value="partial" disabled={isRefundDisabled}><Text strong>{t("bookings.cancel_modal.partial_refund_label")}</Text></Radio>
            <Radio value="no_refund"><Text strong>{t("bookings.cancel_modal.no_refund_label")}</Text></Radio>
          </Flex>
        </Radio.Group>
        {refundType === "full" && <Text type="secondary" className={styles.radioNote}>{t("bookings.cancel_modal.full_refund_note")}</Text>}
        {refundType === "partial" && (
          <Flex vertical gap={4}>
            <Input type="number" placeholder={t("bookings.cancel_modal.partial_amount_placeholder")} value={partialAmount} onChange={handlePartialAmountChange} prefix="$" min={0} status={partialAmountError ? "error" : ""} />
            {partialAmountError && <Text className={styles.errorText}>{partialAmountError}</Text>}
          </Flex>
        )}
        {refundType === "no_refund" && <Text type="secondary" className={styles.radioNote}>{t("bookings.cancel_modal.no_refund_note")}</Text>}
      </Flex>
    </>
  )
}

PaymentBreakdown.propTypes = {
  breakdownData: PropTypes.shape({
    tax_and_fee: PropTypes.number,
    custom_fees: PropTypes.array,
    total_price: PropTypes.number,
    amount_paid_by_guest: PropTypes.number,
    amount_refunded: PropTypes.number,
    refundable_amount: PropTypes.number
  }),
  breakdownLoading: PropTypes.bool,
  refundType: PropTypes.string.isRequired,
  partialAmount: PropTypes.string,
  partialAmountError: PropTypes.string,
  isRefundDisabled: PropTypes.bool,
  handleRadioChange: PropTypes.func.isRequired,
  handlePartialAmountChange: PropTypes.func.isRequired
}

export default PaymentBreakdown
